import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {SortDirection, SortHelper, SortService, TableColumn} from '@app/shared';
import {NzDrawerService} from 'ng-zorro-antd/drawer';
import {
  MaestrosFormDrawerComponent
} from '../../features/maestros/drawers/maestros-form-drawer/maestros-form-drawer.component';
import {combineLatest, Subscription, take} from 'rxjs';
import {MaestrosUtilsService} from '../../features/maestros/services/maestros-utils.service';
import {NzModalService} from 'ng-zorro-antd/modal';
import {
  MaestrosDetailsDrawerComponent
} from 'src/app/features/maestros/drawers/maestros-details-drawer/maestros-details-drawer.component';
import {FlowComponent} from '../../features/maestros/pages/flow/flow.component';
import {
  deepGet,
  DocumentRestrictionsService,
  getSafeImageUrl,
  isExpirationAlert,
  NavigationService,
  ScreenService
} from '@app/core';
import {GlobalValuesService} from '../../core/services/global-values.service';
import {
  ConfirmationModalComponent,
  ConfirmationModalConfig
} from '../../shared/components/confirmation-modal/confirmation-modal.component';

export interface PermissionsMaestros {
  create: boolean,
  edit: boolean,
  delete: boolean,
  viewDetails: boolean,
}

@Component({
  selector: 'app-layout-maestro',
  standalone: false,

  templateUrl: './layout-maestro.component.html',
  styleUrl: './layout-maestro.component.less'
})
export class LayoutMaestroComponent implements OnInit, OnDestroy {

  private _maestroName: string = 'Sin nombre';
  @Input()
  set maestroName(value: string) {
    this._maestroName = value;
    this.rebuildAssignmentWarningCache();
  }

  get maestroName(): string {
    return this._maestroName;
  }
  @Input() columnsData: TableColumn[] = [];
  columnsDataToShow: TableColumn[] = []
  @Input() showDetails?: boolean = true;
  @Input() permissions: PermissionsMaestros = {
    create: true,
    edit: true,
    delete: true,
    viewDetails: true,
  }
  @Input() secondaryBtnText: string | undefined;
  @Input() customDrawer?: {
    formComponent?: any;
    formComponentWidth?:number
    detailsComponent?: any;
    detailsComponentWidth?:number
  };
  @Input() isFlow: boolean = false;
  @Output() createEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() updateEvent: EventEmitter<any> = new EventEmitter<any>();
  @Output() deleteEvent: EventEmitter<number> = new EventEmitter<number>();
  @Output() secondaryBtnClick: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('confirmationModal') confirmationModal!: ConfirmationModalComponent;
  private pendingDeleteId?: number;
  private assignmentWarningCache = new Map<number, boolean>();
  private loadingSubscription?: Subscription;
  private documentTypesSubscription?: Subscription;
  formDrawerRef: any;
  detailsDrawerRef: any;
  searchValue: string = '';
  filteredData: any[] = [];
  viewImageModalRef: any
  @ViewChild('viewImageModalTmpl') viewImageModalTmpl: any;
  tmpImage: string = '';

  getSafeImageUrl = getSafeImageUrl;

  showSearch: boolean = true;
  showGlobalSearch: boolean = false

  allSelected = false;
  indeterminate = false;
  selectedIds: number[] = [];

  constructor(
    private drawerService: NzDrawerService,
    public ms: MaestrosUtilsService,
    public gs: GlobalValuesService,
    public screenService: ScreenService,
    private modalService: NzModalService,
    public navigationService: NavigationService,
    private sortService: SortService,
    private documentRestrictionsService: DocumentRestrictionsService
  ) {
  }

  private _tableData: any[] = [];

  get tableData() {
    return this._tableData;
  }

  @Input()
  set tableData(value: any[]) {
    this._tableData = value;
    this.filter();
    this.rebuildAssignmentWarningCache();
  }

  ngOnInit(): void {
    this.columnsDataToShow = this.columnsData.filter(column => !(column.noVisibleInTable));
    this.configureSortFunctions();

    // Watcher para cerrar el modal cuando el loading termine
    this.loadingSubscription = this.ms.loading$.subscribe(loading => {
      if (!loading && this.confirmationModal?.config?.processing) {
        this.confirmationModal.config.processing = false;
        this.confirmationModal.forceClose();
      }
    });

    this.documentTypesSubscription = this.gs.documentTypes$
      .subscribe(() => this.rebuildAssignmentWarningCache());
  }

  toggleSearch(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.showGlobalSearch = false;
    this.showSearch = !this.showSearch;
  }

  toggleGlobalSearch(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.showSearch = false;
    this.showGlobalSearch = !this.showGlobalSearch;
  }

  openDeleteConfirmation(elementId: number): void {
    this.pendingDeleteId = elementId;
    const config: ConfirmationModalConfig = {
      title: 'Eliminar elemento',
      message: '¿Estás seguro que deseas eliminar este elemento?',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmButtonType: 'primary',
      confirmButtonDanger: true,
    };
    this.confirmationModal.config = config;
    this.confirmationModal.open();
  }

  onDeleteConfirmed(data: {textValue?: string, dateValue?: Date}): void {
    this.ms.loading = true;
    // Actualizar el estado del modal para mostrar loading
    this.confirmationModal.config.processing = true;
    if (this.pendingDeleteId) {
      this.deleteEvent.emit(this.pendingDeleteId);
    }
  }

  deleteElement(elementId: number): void {
    this.deleteEvent.emit(elementId);
  }

  openFormDrawer(editingItem?: any): void {
    if (this.isFlow) {
      combineLatest({
        isMobile: this.screenService.isMobile$,
        isMedium: this.screenService.isMedium$,
      }).pipe(take(1)).subscribe(({isMobile, isMedium}) => {
        this.formDrawerRef = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzMask: true,
          nzContent: FlowComponent,
          nzData: {
            flowId: editingItem?.id || undefined,
            readonly: false,
          }
        })
      });

    } else {
      combineLatest({
        isMobile: this.screenService.isMobile$,
        isMedium: this.screenService.isMedium$,
      }).pipe(take(1)).subscribe(({isMobile, isMedium}) => {
        this.formDrawerRef = this.drawerService.create({
          nzClosable: false,
          nzMask: true,
          nzWidth: this.customDrawer?.formComponentWidth||'635px',
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzContent: this.customDrawer?.formComponent || MaestrosFormDrawerComponent,
          nzData: {
            columnsData: this.columnsData,
            editingElement: editingItem,
            maestroName: this.maestroName,
          }
        });
      });

    }

    this.formDrawerRef.afterOpen.pipe(take(1)).subscribe(() => {
      this.formDrawerRef.getContentComponent().createEvent.pipe(take(1)).subscribe((value: any) => {
        this.createEvent.emit(value);
        this.refreshAssignmentWarnings();
      });
      this.formDrawerRef.getContentComponent().updateEvent.subscribe((value: any) => {
        this.updateEvent.emit(value);
        this.refreshAssignmentWarnings();
      });

      this.ms.drawerRef = this.formDrawerRef;
    });

    this.formDrawerRef.afterClose.pipe(take(1)).subscribe(() => {
      this.ms.drawerRef = undefined;
      this.formDrawerRef = null;
    });
  }

  filter(): void {
    let tempFilteredData = this.tableData;

    // Obtener todas las propiedades de tipo texto y que se muestren en la tabla
    const toFilterProps: string[] = this.columnsData
      .filter(cd => !cd.noVisibleInTable && (cd.pathData?.type === 'text' || cd.pathData?.type === 'number'))
      .map(cd => cd.pathData!.name);

    // Obtener todas las propiedades anidadas y que se muestren en la tabla
    const nestedProps: TableColumn[] = this.columnsData
      .filter(cd => !cd.noVisibleInTable
        && (cd.pathData?.type === 'select' || cd.pathData?.type === 'multi-select' || cd.pathData?.type === 'avatar-select'));

    // Solo filtrar si hay valor de búsqueda y propiedades
    if (this.searchValue?.trim() && (toFilterProps.length > 0 || nestedProps.length > 0)) {
      const searchTerm = this.searchValue.toLowerCase().trim();

      tempFilteredData = tempFilteredData.filter(item =>
        // Verificar en todas las propiedades de texto
        toFilterProps.some(prop => {
          const value = this.getPropertyValue(item, prop);
          return value?.toString().toLowerCase().includes(searchTerm);
        }) ||
        // Verificar en todas las propiedades anidadas
        nestedProps.some(prop => {
          const currentObj = (prop.optionList || []).find(opt => opt.id === item[prop.pathData!.name]);
          const value = this.getPropertyValue(currentObj, prop.pathData!.nestedPath!);
          return value?.toString().toLowerCase().includes(searchTerm);
        })
      );
    }

    this.filteredData = tempFilteredData;
  }

  findOptionNameById(id: number, list: any[]): string {
    const selected = list.find(item => item.id === id);

    if (selected?.name || selected?.surName) {
      return [selected.name, selected.surName].filter(Boolean).join(' ').trim();
    }

    return selected?.role || '-';
  }

  openViewImageModal(item: any) {
    this.tmpImage = item
    this.viewImageModalRef = this.modalService.create({
      nzTitle: undefined,
      nzClosable: false,
      nzFooter: null,
      nzContent: this.viewImageModalTmpl,
      nzCentered: true,
    })
  }

  openDetailsDrawer(item: any): void {
    if (this.isFlow) {
      combineLatest({
        isMobile: this.screenService.isMobile$,
        isMedium: this.screenService.isMedium$,
      }).pipe(take(1)).subscribe(({isMobile, isMedium}) => {
        this.formDrawerRef = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMask: false,
          nzMaskClosable: false,
          nzContent: FlowComponent,
          nzData: {
            flowId: item?.id || undefined,
            readonly: true,
          }
        })
      });

    } else {
      combineLatest({
        isMobile: this.screenService.isMobile$,
        isMedium: this.screenService.isMedium$,
      }).pipe(take(1)).subscribe(({isMobile, isMedium}) => {
        // Open the details drawer
        this.detailsDrawerRef = this.drawerService.create({
          nzClosable: false,
          nzMask: false,
          nzWidth: this.customDrawer?.detailsComponentWidth||'500px',
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzContent: this.customDrawer?.detailsComponent || MaestrosDetailsDrawerComponent,
          nzData: {
            columnsData: this.columnsData,
            element: item,
            maestroName: this.maestroName,
            editPermissions: this.permissions.edit
          }
        });

        // Handle drawer open and close events
        this.detailsDrawerRef.afterOpen.pipe(take(1)).subscribe(() => {
          this.ms.detailsDrawerRef = this.detailsDrawerRef;

          this.detailsDrawerRef.getContentComponent().openEditDrawerEvent?.pipe(take(1))
            .subscribe((id: number) => {
              const selectedElement = this.tableData.find(i => i.id === id);
              this.detailsDrawerRef.close();
              this.openFormDrawer(selectedElement);
            })
        });

        this.detailsDrawerRef.afterClose.pipe(take(1)).subscribe(() => {
          this.ms.detailsDrawerRef = undefined;
        });
      });

    }
  }

  emitSecondaryBtnClick() {
    this.secondaryBtnClick.emit();
  }

  ngOnDestroy() {
    this.loadingSubscription?.unsubscribe();
    this.documentTypesSubscription?.unsubscribe();
    this.formDrawerRef?.close();
    this.detailsDrawerRef?.close();
  }

  // Función auxiliar para obtener valores de propiedades anidadas
  private getPropertyValue(obj: any, path: string): any {
    return path ? path.split('.').reduce((acc, part) => acc?.[part], obj) : '';
  }

    protected readonly isExpirationAlert = isExpirationAlert;

  getDocumentTypeName(documentTypeId: number): string | undefined {
    return  (this.gs.documentTypes$?.value || []).find(dt => dt.id === documentTypeId)?.name;
  }

  protected readonly console = console;
  protected readonly deepGet = deepGet;

  setCurrencyData(rate: number, currencyIso: string, isReverse: boolean): {firstIso: string, rate: number, lastIso: string} {
    if (isReverse) {
      return {
        firstIso: currencyIso,
        rate: 1 / rate,
        lastIso: 'USD'
      }
    } else {
      return {
        firstIso: 'USD',
        rate: rate,
        lastIso: currencyIso
      }
    }
  }

  // Métodos para el sistema de ordenamiento
  private configureSortFunctions(): void {
    SortHelper.configureSortFunctions(this.columnsDataToShow, (column) => this.handleSort(column));
  }

  private handleSort(column: TableColumn): void {
    if (!column.pathData) return;

    const currentDirection = column.sortDirection || null;
    const { sortedData, newDirection } = this.sortService.applySort(
      this.filteredData,
      column,
      currentDirection
    );

    // Resetear otras columnas y actualizar la actual
    SortHelper.resetOtherColumns(this.columnsDataToShow, column);
    column.sortDirection = newDirection;

    // Actualizar los datos filtrados
    this.filteredData = sortedData;
  }

  // Método para obtener la dirección de ordenamiento de una columna
  getColumnSortDirection(column: TableColumn): SortDirection {
    return SortHelper.getColumnSortDirection(column);
  }

  updateSelectionState(): void {
    const totalCount = this.filteredData.length;
    const selectedCount = this.selectedIds.length;

    this.allSelected = totalCount > 0 && selectedCount === totalCount;
    this.indeterminate = selectedCount > 0 && selectedCount < totalCount;
  }

  onSelectAll(checked: boolean): void {
    if (checked) {
      this.selectedIds = this.filteredData.map(item => item.id);
    } else {
      this.selectedIds = [];
    }
    this.updateSelectionState();

    // Emitir evento al componente padre si es necesario
    // this.selectionChange.emit(this.selectedIds);
  }

  onSelectItem(id: number, checked: boolean): void {
    if (checked) {
      this.selectedIds.push(id);
    } else {
      this.selectedIds = this.selectedIds.filter(selectedId => selectedId !== id);
    }
    this.updateSelectionState();

    // Emitir evento al componente padre si es necesario
    // this.selectionChange.emit(this.selectedIds);
  }

  shouldShowAssignmentWarning(item: any, column: TableColumn): boolean {
    if (column.pathData?.name !== 'name') {
      return false;
    }

    return this.assignmentWarningCache.get(item.id) || false;
  }

  getAssignmentRestrictionTooltip(): string {
    return this.maestroName === 'Conductores'
      ? 'Este conductor no tiene los requisitos necesarios por lo que no será posible asignarlo.'
      : 'Este equipo no tiene los requisitos necesarios por lo que no será posible asignarlo.';
  }

  refreshAssignmentWarnings(): void {
    this.rebuildAssignmentWarningCache();
  }

  private rebuildAssignmentWarningCache(): void {
    this.assignmentWarningCache.clear();

    if (this.maestroName !== 'Equipos' && this.maestroName !== 'Conductores') {
      return;
    }

    const documentTypes = this.gs.documentTypes$?.value || [];

    for (const item of this.tableData || []) {
      const hasWarning = this.maestroName === 'Equipos'
        ? !this.documentRestrictionsService.validateEquipment(item, documentTypes).valid
        : !this.documentRestrictionsService.validateDriver(item, documentTypes).valid;

      this.assignmentWarningCache.set(item.id, hasWarning);
    }
  }
}
